import { useApp } from '../../app/AppContext.js';
import { useAsync } from '../lib/useAsync.js';
import {
  Badge,
  Card,
  CardHead,
  ErrorBlock,
  Loading,
  PageHeader,
  Stat,
  StatusDot,
} from '../components/primitives.js';
import { IconGateway, IconLadder, IconQueue } from '../components/icons.js';
import type { GatewayInfo, DunningLadder, QueueSummary, ServiceHealth } from '../../data/port.js';

interface VisaoGeral {
  saude: ServiceHealth[];
  fila: QueueSummary;
  gateway: GatewayInfo;
  regua: DunningLadder;
}

export function Dashboard({ onNavigate }: { onNavigate: (id: string) => void }) {
  const { data } = useApp();
  const estado = useAsync<VisaoGeral>(
    async () => {
      const [saude, fila, gateway, regua] = await Promise.all([
        data.health(),
        data.getQueue(),
        data.getGateway(),
        data.getLadder(),
      ]);
      return { saude, fila, gateway, regua };
    },
    [data],
  );

  return (
    <>
      <PageHeader title="Visão geral" desc="Situação das integrações, da fila de jobs e da cobrança." />

      {estado.loading && !estado.data && <Card><Loading label="Consultando o middleware…" /></Card>}
      {estado.error && <ErrorBlock message={estado.error} />}

      {estado.data && (
        <Conteudo geral={estado.data} onNavigate={onNavigate} />
      )}
    </>
  );
}

function Conteudo({ geral, onNavigate }: { geral: VisaoGeral; onNavigate: (id: string) => void }) {
  const { saude, fila, gateway, regua } = geral;
  const fora = saude.filter((s) => !s.up);
  const n8nFora = fora.some((s) => s.label.toLowerCase() === 'n8n');

  return (
    <>
      <div className="grid grid--stats">
        <Stat
          wide
          label="Integrações no ar"
          value={`${saude.length - fora.length}/${saude.length}`}
          accent={fora.length === 0 ? 'ok' : 'danger'}
          delta={fora.length === 0 ? { text: 'todos no ar', tone: 'ok' } : { text: `${fora.length} fora`, tone: 'danger' }}
        >
          <div className="stat__list">
            {fora.length === 0 ? (
              <span className="faint">Todos os serviços respondendo.</span>
            ) : (
              fora.map((s) => (
                <div className="stat__list-row" key={s.label}>
                  <StatusDot up={false} /> {s.label}
                  <span className="right">{s.detail ?? 'fora'}</span>
                </div>
              ))
            )}
          </div>
        </Stat>
        <Stat
          label="Em voo"
          value={fila.queued + fila.processing}
          accent="info"
          icon={<IconQueue size={15} />}
          bar={[
            { label: 'Na fila', value: fila.queued, tone: 'info' },
            { label: 'Processando', value: fila.processing, tone: 'warn' },
          ]}
        />
        <Stat label="Concluídos" value={fila.done} accent="ok" foot="jobs finalizados" />
        <Stat
          label="Dead-letter"
          value={fila.dead}
          accent={fila.dead > 0 ? 'danger' : 'ok'}
          delta={fila.dead > 0 ? { text: 'requer ação', tone: 'danger' } : { text: 'ok', tone: 'ok' }}
        />
      </div>

      <div className="grid grid--split">
        <Card>
          <CardHead title="Integrações" sub="Health check de cada serviço" />
          <div className="card__body--flush">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Serviço</th>
                    <th>Estado</th>
                    <th className="td--right">Resposta</th>
                    <th>Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {saude.map((s) => (
                    <tr key={s.label}>
                      <td className="td--strong">{s.label}</td>
                      <td>
                        <span className="row" style={{ gap: '0.5ch' }}>
                          <StatusDot up={s.up} />
                          {s.up ? 'no ar' : 'fora'}
                        </span>
                      </td>
                      <td className="td--num">{s.latencyMs === null ? '—' : `${s.latencyMs}ms`}</td>
                      <td className="td--muted">{s.detail ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {n8nFora && (
            <div style={{ padding: '0 1.5rem 1.25rem' }}>
              <div className="notice notice--warn" style={{ marginBottom: 0 }}>
                A régua não dispara enquanto o n8n não responder.
              </div>
            </div>
          )}
        </Card>

        <Card className="card--raise">
          <CardHead title="Resumo operacional" />
          <div className="card__body stack" style={{ gap: 'var(--space-3)' }}>
            <div className="summary-row">
              <span className="summary-row__label">
                <IconGateway size={15} /> Gateway ativo
              </span>
              <span className="summary-row__val">
                <Badge tone="info" dot>{gateway.active}</Badge>
                <button className="btn btn--ghost btn--sm" onClick={() => onNavigate('gateway')}>
                  Gerenciar
                </button>
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-row__label">
                <IconLadder size={15} /> Régua
              </span>
              <span className="summary-row__val">
                <Badge tone={regua.active ? 'ok' : 'warn'} dot>
                  {regua.active ? 'Ativa' : 'Desativada'}
                </Badge>
                <span className="faint">
                  {regua.steps.filter((e) => e.active).length} etapas · teto {regua.dailyCap}/dia
                </span>
                <button className="btn btn--ghost btn--sm" onClick={() => onNavigate('regua')}>
                  Abrir
                </button>
              </span>
            </div>

            {fila.dead > 0 && (
              <div className="summary-row">
                <span className="summary-row__label">
                  <IconQueue size={15} /> Dead-letter
                </span>
                <span className="summary-row__val">
                  <Badge tone="danger" dot>{fila.dead}</Badge>
                  <button className="btn btn--danger-ghost btn--sm" onClick={() => onNavigate('fila')}>
                    Reprocessar
                  </button>
                </span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
